/**
 * Holidays API
 *
 * API endpoints for holidays management operations
 */

import dayjs from "dayjs";
import toast from "react-hot-toast";

import { nextCargoApi } from "../../baseApi";
import type {
  DeleteHolidaysByCompanyArgs,
  GetHolidaysByCompanyArgs,
  GetHolidaysByCompanyResponse,
  HolidaysByCompany,
  SaveHolidaysByCompanyArgs,
  SaveHolidaysByCompanyResponse
} from "./holidays.types";
import { saveHolidaysByCompanyArgsSchema } from "./schemas";

/**
 * Holidays API endpoints
 * Provides operations for holidays management
 */
export const holidaysApi = nextCargoApi.injectEndpoints({
  endpoints: (build) => ({
    /**
     * Get holidays by company ID
     * @param id - Company ID
     * @param formatter - Optional date format (default: YYYY-MM-DD)
     * @returns Holidays with parsed dates and blocked dates map/array
     */
    getHolidaysByCompany: build.query<GetHolidaysByCompanyResponse, GetHolidaysByCompanyArgs, HolidaysByCompany[]>({
      query: ({ id }) => `/holidays/company/${id}`,
      transformResponse: (response, _meta, arg) => {
        const formatter = arg.formatter || "YYYY-MM-DD";
        const parseDates = (holiday: (typeof response)[number]) => JSON.parse(holiday.dates) as string[];

        // Transform holidays with formatted dates
        const holidays = response.map((holiday) => ({
          ...holiday,
          dates: parseDates(holiday).map((date) => dayjs(date, "YYYY-MM-DD").format(formatter))
        }));

        // Create blocked dates map and array for efficient component usage
        const $blockedDatesMap: Record<string, string> = {};
        const $blockedDatesArray: string[] = [];

        holidays.forEach((holiday) => {
          holiday.dates.forEach((dateStr) => {
            $blockedDatesMap[dateStr] = holiday.title;
            $blockedDatesArray.push(dateStr);
          });
        });

        // Return object with holidays and $ properties separated
        return {
          holidays,
          $blockedDatesMap,
          $blockedDatesArray
        };
      },
      onQueryStarted: async (_args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Carregando feriados...",
          success: "Feriados carregados com sucesso!",
          error: "Erro ao carregar feriados."
        });
      }
    }),

    /**
     * Save holiday by company (create or update)
     * @param payload - Holiday data to save
     * @returns Saved holiday with parsed dates
     */
    saveHolidaysByCompany: build.mutation<SaveHolidaysByCompanyResponse, SaveHolidaysByCompanyArgs, HolidaysByCompany & { company: { id: number } }>({
      query: ({ dates, ...body }) => ({
        url: `/holidays/save`,
        method: "POST",
        body: {
          ...body,
          dates: JSON.stringify(dates)
        }
      }),
      argSchema: saveHolidaysByCompanyArgsSchema,
      transformResponse: (response) => {
        return {
          ...response,
          dates: JSON.parse(response.dates) as string[]
        };
      },
      onQueryStarted: async (_args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Salvando feriado...",
          success: "Feriado salvo com sucesso!",
          error: "Erro ao salvar feriado."
        });
      }
    }),

    /**
     * Delete holiday by company
     * @param id - Holiday ID
     * @returns void
     */
    deleteHolidaysByCompany: build.mutation<void, DeleteHolidaysByCompanyArgs>({
      query: ({ id }) => ({
        url: `/holidays/delete/${id}`,
        method: "DELETE"
      }),
      onQueryStarted: async (_args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Excluindo feriado...",
          success: "Feriado excluído com sucesso!",
          error: "Erro ao excluir feriado."
        });
      }
    })
  })
});

/**
 * Enhance endpoints with cache tag configuration
 */
holidaysApi.enhanceEndpoints({
  addTagTypes: ["HolidaysByCompany"],
  endpoints: {
    getHolidaysByCompany: {
      providesTags: (result, _error, { id }) =>
        result?.holidays
          ? [...result.holidays.map(({ id }) => ({ type: "HolidaysByCompany" as const, id })), { type: "HolidaysByCompany", id }]
          : [{ type: "HolidaysByCompany", id: "LIST" }]
    },
    saveHolidaysByCompany: {
      invalidatesTags: () => ["HolidaysByCompany"]
    },
    deleteHolidaysByCompany: {
      invalidatesTags: (_result, _error, { id }) => [{ type: "HolidaysByCompany", id }]
    }
  }
});
